import { Building2Icon } from 'lucide-react';
import { createElement, type ComponentType } from 'react';

import { PERMISSIONS, useHasPermission } from '@/shared/auth';
import { defineModule } from '@/shared/registry/types';

import { useInvitationCounts } from './api';
import { companyCommandSources } from './commands';
import { AbsenceNotification } from './components/AbsenceNotification';
import { DepartmentSearchResult } from './components/DepartmentSearchResult';
import { AboutTabEntry, DepartmentPanelEntry, SecurityTabEntry, UserPanelEntry } from './entries';
import { registerCompanyRealtime } from './realtime';

const page = (load: () => Promise<{ default: ComponentType }>) => async () => ({
  Component: (await load()).default,
});

/**
 * Sidebar badge for whoever manages invitations (`identity.invitations.manage`): invitations that expired without being accepted (resend or revoke them). Kept live
 * by the `identity.invitations-changed` realtime event.
 */
function useCompanyCounter(): number | undefined {
  const canSee = useHasPermission(PERMISSIONS.identity.invitationsManage);
  const counts = useInvitationCounts({ enabled: canSee });
  return canSee && counts.data && counts.data.expired > 0 ? counts.data.expired : undefined;
}

/**
 * `company` module — the people side of the portal (docs/modules/company.md): the employee directory (`/company`), the
 * org chart (`/company/structure`), profiles (`/company/users/:userId` with every module's profile tabs), the absence
 * chart (`/company/absences`) and, for administrators, users, invitations and departments (`/company/admin/…`); side
 * panels `user:<id>` and `department:<id>`.
 */
export const module = defineModule({
  id: 'company',
  routes: [
    {
      path: 'company',
      children: [
        { index: true, lazy: page(() => import('./pages/DirectoryPage')) },
        { path: 'structure', lazy: page(() => import('./pages/StructurePage')) },
        { path: 'absences', lazy: page(() => import('./pages/AbsencesPage')) },
        { path: 'users/:userId', lazy: page(() => import('./pages/ProfilePage')) },
        { path: 'admin/users', lazy: page(() => import('./pages/admin/UsersAdminPage')) },
        { path: 'admin/invitations', lazy: page(() => import('./pages/admin/InvitationsAdminPage')) },
        { path: 'admin/departments', lazy: page(() => import('./pages/admin/DepartmentsAdminPage')) },
        { path: '*', lazy: page(() => import('./pages/CompanyNotFound')) },
      ],
    },
  ],
  nav: [
    {
      id: 'company',
      labelKey: 'company:nav.title',
      icon: Building2Icon,
      to: '/company',
      order: 70,
      section: 'main',
      useCounter: useCompanyCounter,
    },
  ],
  profileTabs: [
    {
      id: 'about',
      labelKey: 'company:profile.tab',
      order: 10,
      element: (userId) => createElement(AboutTabEntry, { userId }),
    },
    {
      // Password, two-factor authentication and sessions of the own account (an administrator reaches another account's
      // security from the users table or the profile's admin menu: a dialog, not a tab of every profile).
      id: 'security',
      labelKey: 'company:profile.securityTab',
      order: 900,
      visible: ({ self }) => self,
      element: (userId) => createElement(SecurityTabEntry, { userId }),
    },
  ],
  sidePanels: [
    { type: 'user', component: UserPanelEntry, size: 'md', titleKey: 'company:panel.userTitle' },
    {
      type: 'department',
      component: DepartmentPanelEntry,
      size: 'md',
      titleKey: 'company:panel.departmentTitle',
    },
  ],
  searchRenderers: { department: DepartmentSearchResult },
  notificationRenderers: { 'identity.absence-created': AbsenceNotification },
  commands: companyCommandSources,
  setup: ({ realtime, queryClient }) => {
    registerCompanyRealtime(realtime, queryClient);
  },
});
